"use client";

import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { useCaseIndex, type CaseIndex } from "@/lib/caseIndex";
import LinkedText from "@/components/LinkedText";

/** Parsed HD judgment paragraph as returned by GET /case/{ref_id}. */
interface CaseParagraph {
  ord: string | null;
  heading: string | null;
  kind: "paragraph" | "heading" | "dissent";
  text: string;
}

interface CaseDetail {
  ref_id: string;
  display_ref: string;
  popular_name?: string | null;
  court_code?: string | null;
  decision_date?: string | null;
  rattsomraden?: string[];
  lagrum?: string[];
  nyckelord?: string[];
  summary?: string;
  pdf_url?: string | null;
  pdf_filename?: string | null;
  api_url?: string;
  fetched_at?: string | null;
  parsed?: {
    composition?: string | null;
    date_str?: string | null;
    majority: CaseParagraph[];
    dissents: CaseParagraph[][];
    dissent_kinds?: string[];
    dissent_justices?: string[];
  } | null;
  /** Lower-court decisions embedded before the HD opinion. */
  lower_courts?: {
    court: string;
    paragraphs: CaseParagraph[];
  }[];
  raw_html?: string | null;
}

/** Court code → full Swedish name for the majority header. */
const COURT_NAMES: Record<string, string> = {
  HDO: "Högsta domstolen",
  HFD: "Högsta förvaltningsdomstolen",
  AD: "Arbetsdomstolen",
  MD: "Marknadsdomstolen",
  MOD: "Marknads- och miljööverdomstolen",
  MIOD: "Migrationsöverdomstolen",
  RA: "Regeringsrätten",
};
function courtName(code: string | null | undefined): string {
  return (code && COURT_NAMES[code]) || code || "Högsta domstolen";
}

/**
 * Full judgment text (GET /case/{ref_id}) rendered for the "Dom" tab.
 * Fetches on mount; shows the parsed HD opinion (majority + dissents) when
 * available, otherwise the official summary with a link to the PDF.
 */
export default function CaseText({
  refId,
  getToken,
  onOpenCase,
}: {
  refId: string;
  getToken: () => Promise<string | null>;
  /** Open a referenced case in its own Dom tab (owned by the page). */
  onOpenCase?: (refId: string, displayRef: string) => void;
}) {
  const [data, setData] = useState<CaseDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const index = useCaseIndex();

  useEffect(() => {
    setData(null);
    setLoadError(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/case/${encodeURIComponent(refId)}`, {
          headers: { "Content-Type": "application/json" },
          token: (await getToken()) ?? undefined,
        });
        if (cancelled) return;
        if (!res.ok) {
          setLoadError(res.status === 401 ? "Kräver inloggning." : `HTTP ${res.status}`);
          return;
        }
        setData((await res.json()) as CaseDetail);
      } catch {
        if (!cancelled) setLoadError("Kunde inte hämta domen.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refId, getToken]);

  if (loadError) {
    return (
      <div className="card card--sm card--flat case-panel">
        <p className="case-muted">{loadError}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card card--sm card--flat case-panel">
        <p className="case-muted">Hämtar dom…</p>
      </div>
    );
  }
  if (data.parsed) {
    return (
      <div className="card card--sm card--flat case-panel">
        <CaseParsedBody data={data} index={index} onOpenCase={onOpenCase} />
      </div>
    );
  }
  return (
    <div className="card card--sm card--flat case-panel">
      <p className="case-muted">
        <LinkedText
          text={data.summary || "Fulltexten är inte strukturerad för visning."}
          index={index}
          onOpenCase={onOpenCase}
          skipRef={data.ref_id}
        />
        {data.pdf_url && (
          <>
            {" "}
            <a href={data.pdf_url} target="_blank" rel="noreferrer">
              Öppna PDF ↗
            </a>
          </>
        )}
      </p>
    </div>
  );
}

function CaseParsedBody({
  data,
  index,
  onOpenCase,
}: {
  data: CaseDetail;
  index: CaseIndex | null;
  onOpenCase?: (refId: string, displayRef: string) => void;
}) {
  const meta: string[] = [];
  if (data.court_code) meta.push(data.court_code);
  if (data.decision_date) meta.push(data.decision_date);
  if (data.popular_name) meta.push(data.popular_name as string);
  return (
    <div className="case-body">
      {meta.length > 0 && <div className="case-meta">{meta.join(" · ")}</div>}
      {data.summary && (
        <div className="callout case-summary">
          <span className="case-summary-label">Sammanfattning</span>
          <p>
            <LinkedText
              text={data.summary}
              index={index}
              onOpenCase={onOpenCase}
              skipRef={data.ref_id}
            />
          </p>
        </div>
      )}
      {data.lower_courts && data.lower_courts.length > 0 && (
        <>
          {data.lower_courts.map((lc, i) => (
            <details className="card card--sm card--flat case-court-box" key={i}>
              <summary>{lc.court}</summary>
              <div className="case-court-body">
                {lc.paragraphs.map((p, j) => (
                  <CaseParagraphEl key={j} p={p} index={index} onOpenCase={onOpenCase} skipRef={data.ref_id} />
                ))}
              </div>
            </details>
          ))}
        </>
      )}
      {(data.parsed?.majority.length ?? 0) > 0 && (
        <details className="card card--sm card--flat case-court-box case-majority" open>
          <summary>
            <span className="case-court-name">{courtName(data.court_code)}</span>
            {data.parsed?.composition && (
              <span className="case-court-justices">{data.parsed.composition}</span>
            )}
          </summary>
          <div className="case-court-body">
            {data.parsed!.majority.map((p, i) => (
              <CaseParagraphEl key={i} p={p} index={index} onOpenCase={onOpenCase} skipRef={data.ref_id} />
            ))}
          </div>
        </details>
      )}
      {data.parsed?.dissents.map((grp, gi) => {
        const kind = data.parsed?.dissent_kinds?.[gi] === "tillägg" ? "tillägg" : "skiljaktig";
        const label = kind === "tillägg" ? "Tillägg för egen del" : "Skiljaktig mening";
        const n = data.parsed?.dissents.length ?? 1;
        const numbered = n > 1 ? ` ${gi + 1}` : "";
        const justices = data.parsed?.dissent_justices?.[gi];
        return (
          <details className="card card--sm card--flat case-court-box case-dissent" key={gi}>
            <summary>
              <span className="case-court-name">{label}{numbered}</span>
              {justices && <span className="case-court-justices">{justices}</span>}
            </summary>
            <div className="case-court-body">
              {grp.map((p, i) => (
                <CaseParagraphEl key={i} p={p} index={index} onOpenCase={onOpenCase} skipRef={data.ref_id} />
              ))}
            </div>
          </details>
        );
      })}
      {(data.pdf_url || data.api_url) && (
        <div className="case-footer">
          {data.pdf_url && (
            <a href={data.pdf_url} target="_blank" rel="noreferrer">
              Ladda ner PDF ↗
            </a>
          )}
          {data.api_url && (
            <a href={data.api_url} target="_blank" rel="noreferrer">
              Officiell källa (Sveriges Domstolar) ↗
            </a>
          )}
          {data.fetched_at && (
            <span className="case-muted">Hämtad {data.fetched_at.slice(0, 10)}</span>
          )}
        </div>
      )}
    </div>
  );
}

function CaseParagraphEl({
  p,
  index,
  onOpenCase,
  skipRef,
}: {
  p: CaseParagraph;
  index: CaseIndex | null;
  onOpenCase?: (refId: string, displayRef: string) => void;
  skipRef?: string | null;
}) {
  if (p.kind === "heading") {
    return <div className="case-heading">{p.text}</div>;
  }
  if (p.ord) {
    return (
      <div className="case-para" id={`p${p.ord}`}>
        <span className="case-num">{p.ord}.</span>
        <span className="case-text">
          <LinkedText text={p.text} index={index} onOpenCase={onOpenCase} skipRef={skipRef} />
        </span>
      </div>
    );
  }
  return (
    <p className="case-para">
      <LinkedText text={p.text} index={index} onOpenCase={onOpenCase} skipRef={skipRef} />
    </p>
  );
}
